// Importing React hooks for state management and lifecycle
import { useState, useEffect } from 'react';
// Importing pre-configured Axios client for making API requests
import axiosClient from '../api/axiosClient';
// Importing TypeScript types for strong typing of data structures
import type { User, Course, Deadline, StatusUpdate, Notification } from '../types';

// Defining the structure of the data returned by useHomeData hook
interface HomeData {
  // Courses the user is enrolled in
  enrolledCourses: Course[];
  // Courses available to enroll in
  availableCourses: Course[];
  // Upcoming deadlines for enrolled courses
  deadlines: Deadline[];
  // Recent status updates from other users
  updates: StatusUpdate[];
  // Notifications relevant to the user
  notifications: Notification[];
  // Error message if fetching data fails
  error: string | null;
  // Setter for notifications state
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  // Setter for enrolled courses state
  setEnrolledCourses: React.Dispatch<React.SetStateAction<Course[]>>;
  // Setter for available courses state
  setAvailableCourses: React.Dispatch<React.SetStateAction<Course[]>>;
  // Function to manually refresh data
  refreshData: () => Promise<void>;
}
// Custom hook to manage home dashboard data
export function useHomeData(user: User | null, isLoading: boolean): HomeData {
  // State variables for storing dashboard data
  const [enrolledCourses, setEnrolledCourses] = useState<Course[]>([]);
  const [availableCourses, setAvailableCourses] = useState<Course[]>([]);
  const [deadlines, setDeadlines] = useState<Deadline[]>([]);
  const [updates, setUpdates] = useState<StatusUpdate[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Function to fetch dashboard data from backend
  const fetchData = async () => {
    // Skip fetching if user not ready or still loading
    if (!user || isLoading) {
      return;
    }
    try {
      // Fetch all courses from backend
      const coursesResponse = await axiosClient.get('/elearning/api/courses/', {
        headers: { 'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '' }, // Send CSRF token
      });
      // Ensure the response is an array, otherwise default to empty
      const coursesData = Array.isArray(coursesResponse.data) ? coursesResponse.data : [];
      // Variables to track enrolled courses
      let enrolledCourseIds: number[] = [];
      let enrolledCoursesData: Course[] = [];
      // If user is a student, fetch their enrollments
      if (user.role === 'student') {
        try {
          const enrollmentsResponse = await axiosClient.get('/elearning/api/enrollments/', {
            headers: { 'X-CSRFToken': document.cookie.match(/csrftoken=([^;]+)/)?.[1] || '' },
          });
          // Ensure enrollments is an array before processing
          if (Array.isArray(enrollmentsResponse.data)) {
            // Normalize enrolled course data
            enrolledCoursesData = enrollmentsResponse.data
              .filter(
                (e: any) => e.course && (typeof e.course.id === 'number' || typeof e.course.id === 'string')
              )
              .map((e: any) => ({
                ...e.course,
                id: Number(e.course.id), // Ensure numeric ID
                enrolled: true,
                slug: e.course.slug || `course-${e.course.id}`, // Generate slug if missing
                teacher: {
                  ...e.course.teacher,
                  id: Number(e.course.teacher.id),
                  email: e.course.teacher.email || null,
                  real_name: e.course.teacher.real_name || null,
                  photo: e.course.teacher.photo || null,
                },
              }));
            // Extract enrolled course IDs for filtering
            enrolledCourseIds = enrolledCoursesData.map((course: Course) => course.id);
          } else {
            console.warn('[useHomeData] Enrollments response is not an array:', enrollmentsResponse.data);
          }
        } catch (enrollErr: any) {
          // Log enrollment errors but recover gracefully
          console.error(
            '[useHomeData] Enrollments error:',
            JSON.stringify(enrollErr.response?.data || enrollErr.message, null, 2)
          );
          enrolledCoursesData = [];
          enrolledCourseIds = [];
        }
        // Set enrolled courses state
        setEnrolledCourses(enrolledCoursesData);
        // Filter available courses (exclude enrolled ones, validate data)
        const available = coursesData
          .filter((course: any) => {
            if (!course || !course.id || !course.teacher) {
              console.warn('[useHomeData] Invalid course data:', course);
              return false;
            }
            const courseId = Number(course.id);
            const isEnrolled = enrolledCourseIds.includes(courseId);
            return !isEnrolled; // Keep only non-enrolled courses
          })
          .map((course: any) => ({
            ...course,
            id: Number(course.id),
            enrolled: false, // Available courses are not yet enrolled
            slug: course.slug || `course-${course.id}`,
            teacher: {
              ...course.teacher,
              id: Number(course.teacher.id),
              email: course.teacher.email || null,
              real_name: course.teacher.real_name || null,
              photo: course.teacher.photo || null,
            },
          }));
        // Set available courses state
        setAvailableCourses(available);
      }
      // If user is a teacher, show only the courses they own
      else if (user.role === 'teacher') {
        const teacherCourses = coursesData
          .filter((course: any) => course.teacher && Number(course.teacher.id) === user.id)
          .map((course: any) => ({
            ...course,
            id: Number(course.id),
            enrolled: false, // Teachers are not "enrolled", they own the course
            slug: course.slug || `course-${course.id}`,
            teacher: {
              ...course.teacher,
              id: Number(course.teacher.id),
              email: course.teacher.email || null,
              real_name: course.teacher.real_name || null,
              photo: course.teacher.photo || null,
            },
          }));
        // Set teacher’s courses as "enrolled" list and clear available list
        setEnrolledCourses(teacherCourses);
        setAvailableCourses([]);
      }
      // ---------------------- DEADLINES, STATUS UPDATES, NOTIFICATIONS ----------------------
// Fetch deadlines (e.g., assignment or project due dates)
try {
  // Call the API endpoint for deadlines
  const deadlinesResponse = await axiosClient.get('/elearning/api/deadlines/');
  // Map and normalize deadlines into a consistent format
  const mappedDeadlines = Array.isArray(deadlinesResponse.data)
    ? deadlinesResponse.data
        // Filter out invalid or incomplete records
        .filter((d: any) => d && d.id && d.due_date)
        // Transform each deadline object to ensure numeric IDs and normalized creator info
        .map((d: any) => ({
          ...d,
          id: Number(d.id),
          created_by: {
            ...d.created_by,
            id: Number(d.created_by.id),
            email: d.created_by.email || null,
            real_name: d.created_by.real_name || null,
            photo: d.created_by.photo || null,
          },
        }))
    : [];
  // Store deadlines in state
  setDeadlines(mappedDeadlines);
} catch (deadlinesErr: any) {
  // Log error and fallback to empty list
  console.error(
    '[useHomeData] Deadlines error:',
    JSON.stringify(deadlinesErr.response?.data || deadlinesErr.message, null, 2)
  );
  setDeadlines([]);
}
// Fetch status updates (e.g., activity feed or progress updates)
try {
  const updatesResponse = await axiosClient.get('/elearning/api/status_updates/');
  // Map updates to normalized objects
  setUpdates(
    Array.isArray(updatesResponse.data)
      ? updatesResponse.data.map((u: any) => ({
          ...u,
          id: Number(u.id),
          user: {
            ...u.user,
            id: Number(u.user.id),
            email: u.user.email || null,
            real_name: u.user.real_name || null,
            photo: u.user.photo || null,
          },
        }))
      : []
  );
      } catch (updatesErr: any) {
  // Log error and fallback to empty list
  console.error(
    '[useHomeData] Status updates error:',
    JSON.stringify(updatesErr.response?.data || updatesErr.message, null, 2)
  );
  setUpdates([]);
      }
// Fetch notifications (alerts, reminders, announcements, etc.)
try {
  const notificationsResponse = await axiosClient.get('/elearning/api/notifications/');
  // Normalize notifications
  setNotifications(
    Array.isArray(notificationsResponse.data)
      ? notificationsResponse.data.map((n: any) => ({
          ...n,
          id: Number(n.id),
          // Default timestamp if missing
          timestamp: n.timestamp || new Date().toISOString(),
          // Ensure boolean for "is_read"
          is_read: n.is_read || false,
        }))
      : []
  );
      } catch (notifErr: any) {
  // Log error and fallback to empty list
  console.error(
    '[useHomeData] Notifications error:',
    JSON.stringify(notifErr.response?.data || notifErr.message, null, 2)
  );
  setNotifications([]);
      }
// Reset global error state if all API calls succeed
  setError(null);
    } catch (err: any) {
  // Handle unexpected or global errors in the entire fetchData process
  console.error(
    '[useHomeData] Global error:',
    JSON.stringify(err.response?.data || err.message, null, 2)
  );
  setError(err.response?.data?.error || 'Failed to fetch data');
    }
  };
// ---------------------- HOOK EXECUTION ----------------------
// Trigger data fetching when user or loading state changes
useEffect(() => {
  fetchData();
  }, [user, isLoading]);
// Return data and state-modifying functions for consumers of the hook
return {
  enrolledCourses,      // List of courses user is enrolled in
  availableCourses,     // List of courses user can join (students) or empty (teachers)
  deadlines,            // Upcoming deadlines
  updates,              // Recent status updates
  notifications,        // Notifications for the user
  error,                // Any fetch error
  setNotifications,     // Allow manual updating of notifications state
  setEnrolledCourses,   // Allow manual updating of enrolled courses state
  setAvailableCourses,  // Allow manual updating of available courses state
  refreshData: fetchData, // Public method to refresh all data manually
     };
}
